/**
 * myCargoCFS Landing Page
 * Public landing page for the warehouse management system
 */

import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Warehouse,
  Truck,
  Package,
  Camera,
  FileText,
  Shield,
  Clock,
  Globe,
  ChevronRight,
  CheckCircle,
  ArrowRight,
  MapPin,
  Phone,
  Mail,
  ExternalLink,
  Code,
  Zap,
  Terminal,
  Copy,
  Calculator,
  Loader2,
  X,
  Sparkles,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { useState, useEffect, useMemo } from "react";
import { API_URL } from "../config/api";


export default function WMSLandingPage() {
  // ===== Rate Calculator State =====
  const [calcInputs, setCalcInputs] = useState({
    pallets: 10,
    storage_days: 15,
    handling_inbound: true,
    handling_outbound: true,
    stretch_foil_pallets: 0,
    labeling_pallets: 0,
    include_documentation: true,
  });
  const [calcResult, setCalcResult] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcError, setCalcError] = useState(null);

  // Debounced live calculation
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        setCalcLoading(true);
        setCalcError(null);
        const resp = await fetch(`${API_URL}/wms/public/quote-calculate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(calcInputs),
        });
        if (!resp.ok) throw new Error((await resp.json()).detail || "Calculation failed");
        const data = await resp.json();
        if (!cancelled) setCalcResult(data);
      } catch (e) {
        if (!cancelled) setCalcError(e.message || "Could not calculate");
      } finally {
        if (!cancelled) setCalcLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [calcInputs]);

  // ===== Lead Modal State =====
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: "", email: "", phone: "", company: "", notes: "" });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadResult, setLeadResult] = useState(null);

  const submitLead = async () => {
    if (!leadForm.name.trim() || leadForm.name.trim().length < 2) {
      toast.error("Please enter your full name");
      return;
    }
    if (!leadForm.email.trim() || !leadForm.email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    try {
      setLeadSubmitting(true);
      const payload = {
        ...leadForm,
        ...calcInputs,
        calculated_total_eur: calcResult?.total_eur ?? null,
      };
      const resp = await fetch(`${API_URL}/wms/public/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "Submission failed");
      setLeadResult(data);
      toast.success(`Request sent — reference ${data.lead_number}`);
    } catch (e) {
      toast.error(e.message || "Something went wrong");
    } finally {
      setLeadSubmitting(false);
    }
  };

  const handlingLabel = useMemo(() => {
    const parts = [];
    if (calcInputs.handling_inbound) parts.push("inbound");
    if (calcInputs.handling_outbound) parts.push("outbound");
    return parts.length ? parts.join(" + ") : "none";
  }, [calcInputs.handling_inbound, calcInputs.handling_outbound]);

  const services = [
    {
      icon: Warehouse,
      title: "Warehousing & Storage",
      description: "Secure customs bonded warehouse with 24/7 surveillance and climate control options",
    },
    {
      icon: Package,
      title: "Transloading",
      description: "Efficient cross-docking and consolidation services for import/export cargo",
    },
    {
      icon: Truck,
      title: "Distribution",
      description: "Last-mile delivery and regional distribution network across Slovenia and EU",
    },
    {
      icon: FileText,
      title: "Customs Clearance",
      description: "Full customs brokerage services including T1/T2 documentation",
    },
    {
      icon: Camera,
      title: "Photo Documentation",
      description: "Real-time photo capture of cargo for transparency and damage prevention",
    },
    {
      icon: Shield,
      title: "Insurance & Security",
      description: "Comprehensive cargo insurance and secure storage facilities",
    },
  ];

  const additionalServices = [
    "Palletisation",
    "Stretch foiling",
    "Labeling & marking",
    "Quality inspection",
    "Damage reports",
    "Warehouse receipts",
    "Pick & pack",
    "Kitting services",
  ];

  const stats = [
    { value: "10,000+", label: "sq.m warehouse space" },
    { value: "500+", label: "transloads per month" },
    { value: "24/7", label: "security monitoring" },
    { value: "100%", label: "customs bonded" },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/wms" className="flex items-center gap-3">
            <svg viewBox="0 0 56 48" className="w-11 h-10" xmlns="http://www.w3.org/2000/svg" aria-label="Cargo CFS logo">
              {/* Warehouse roof */}
              <path d="M4 22 L28 6 L52 22" fill="none" stroke="#1e6dd8" strokeWidth="2.5" strokeLinejoin="round" />
              {/* Door + green square */}
              <rect x="22" y="22" width="12" height="18" fill="none" stroke="#1e6dd8" strokeWidth="2.5" />
              <rect x="24" y="24" width="8" height="7" fill="#6fbf3a" />
              {/* Left / right walls */}
              <line x1="8" y1="22" x2="8" y2="40" stroke="#1e6dd8" strokeWidth="2.5" />
              <line x1="48" y1="22" x2="48" y2="40" stroke="#1e6dd8" strokeWidth="2.5" />
              {/* Floor / wheels */}
              <line x1="4" y1="40" x2="52" y2="40" stroke="#1e6dd8" strokeWidth="2.5" />
              <circle cx="40" cy="42" r="2.2" fill="#1e6dd8" />
              <circle cx="46" cy="42" r="2.2" fill="#1e6dd8" />
            </svg>
            <div>
              <span className="font-bold text-xl text-slate-900 tracking-tight">Cargo<span className="text-blue-600">CFS</span></span>
              <span className="text-[11px] text-blue-600 italic block -mt-0.5 tracking-wide">storage simplified</span>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <a
              href="#calculator"
              className="text-slate-600 hover:text-blue-600 text-sm font-medium"
            >
              Rate Calculator
            </a>
            <a
              href="#api"
              className="text-slate-600 hover:text-blue-600 text-sm font-medium"
            >
              API Integration
            </a>
            <a
              href="https://cargo-cfs.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-600 hover:text-blue-600 flex items-center gap-1 text-sm"
            >
              cargo-cfs.com
              <ExternalLink className="w-3 h-3" />
            </a>
            <Link to="/wms/customer">
              <Button variant="outline">Customer Login</Button>
            </Link>
            <Link to="/wms/app">
              <Button>Staff Portal</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                Public Customs Bonded Warehouse
              </span>
              <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-6">
                Your Trusted Partner for{" "}
                <span className="text-blue-600">Cargo Handling</span> in Slovenia
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                Cargo CFS is a public customs bonded warehouse in Dekani, Slovenia —
                warehousing, transloading, distribution, and customs brokerage under one roof.
                Track your shipments 24/7 and request services online.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/wms/customer">
                  <Button size="lg" className="gap-2">
                    Track Your Shipment
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <a href="https://cargo-cfs.com" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="gap-2">
                    Visit cargo-cfs.com
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white rounded-2xl shadow-2xl p-8 border">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  Real-time Shipment Status
                </h3>
                <div className="space-y-4">
                  {[
                    { status: "Arrived at warehouse", time: "Today, 09:45", done: true },
                    { status: "Customs clearance", time: "Today, 11:30", done: true },
                    { status: "Quality inspection", time: "In progress", done: false },
                    { status: "Ready for pickup", time: "Pending", done: false },
                  ].map((step, idx) => (
                    <div key={idx} className="flex items-center gap-4">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          step.done ? "bg-green-100 text-green-600" : "bg-slate-100 text-slate-400"
                        }`}
                      >
                        {step.done ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <span className="w-2 h-2 rounded-full bg-current" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className={`font-medium ${step.done ? "text-slate-900" : "text-slate-500"}`}>
                          {step.status}
                        </p>
                        <p className="text-sm text-slate-400">{step.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Decorative elements */}
              <div className="absolute -z-10 top-4 -right-4 w-full h-full bg-blue-200 rounded-2xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <p className="text-3xl lg:text-4xl font-bold text-white">{stat.value}</p>
                <p className="text-blue-100 mt-1">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rate Calculator */}
      <section id="calculator" className="py-20 px-4 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              Instant Indicative Quote
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Calculate Your CargoCFS Rate
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Enter your pallet count and storage duration — get an indicative price in seconds.
              Request a formal offer and Mateja will come back within 24 business hours.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8">
            {/* Inputs */}
            <div className="lg:col-span-3 bg-white border rounded-2xl p-6 lg:p-8 shadow-sm">
              <h3 className="font-semibold text-lg text-slate-900 mb-6 flex items-center gap-2">
                <Calculator className="w-5 h-5 text-blue-600" />
                Your cargo & services
              </h3>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="calc-pallets" className="text-sm font-medium text-slate-700">
                    Euro pallets (1.2 × 0.8 m)
                  </Label>
                  <Input
                    id="calc-pallets"
                    data-testid="calc-pallets-input"
                    type="number"
                    min={1}
                    max={500}
                    value={calcInputs.pallets}
                    onChange={(e) =>
                      setCalcInputs({ ...calcInputs, pallets: Math.max(1, Math.min(500, parseInt(e.target.value || "0", 10) || 0)) })
                    }
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="calc-days" className="text-sm font-medium text-slate-700">
                    Storage days
                  </Label>
                  <Input
                    id="calc-days"
                    data-testid="calc-days-input"
                    type="number"
                    min={0}
                    max={365}
                    value={calcInputs.storage_days}
                    onChange={(e) =>
                      setCalcInputs({ ...calcInputs, storage_days: Math.max(0, Math.min(365, parseInt(e.target.value || "0", 10) || 0)) })
                    }
                    className="mt-1.5"
                  />
                </div>
              </div>

              <div className="mt-6 border-t pt-6">
                <div className="font-medium text-sm text-slate-700 mb-4">Handling</div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-lg border bg-slate-50">
                    <div>
                      <div className="text-sm font-medium text-slate-900">Inbound handling</div>
                      <div className="text-xs text-slate-500">Receive, unload, putaway</div>
                    </div>
                    <Switch
                      data-testid="calc-inbound-switch"
                      checked={calcInputs.handling_inbound}
                      onCheckedChange={(v) => setCalcInputs({ ...calcInputs, handling_inbound: v })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg border bg-slate-50">
                    <div>
                      <div className="text-sm font-medium text-slate-900">Outbound handling</div>
                      <div className="text-xs text-slate-500">Pick, load, dispatch</div>
                    </div>
                    <Switch
                      data-testid="calc-outbound-switch"
                      checked={calcInputs.handling_outbound}
                      onCheckedChange={(v) => setCalcInputs({ ...calcInputs, handling_outbound: v })}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 border-t pt-6">
                <div className="font-medium text-sm text-slate-700 mb-4">Value-added services</div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="calc-foil" className="text-sm text-slate-700">
                      Stretch-foiled pallets
                    </Label>
                    <Input
                      id="calc-foil"
                      data-testid="calc-foil-input"
                      type="number"
                      min={0}
                      max={calcInputs.pallets}
                      value={calcInputs.stretch_foil_pallets}
                      onChange={(e) =>
                        setCalcInputs({
                          ...calcInputs,
                          stretch_foil_pallets: Math.max(0, parseInt(e.target.value || "0", 10) || 0),
                        })
                      }
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="calc-label" className="text-sm text-slate-700">
                      Labeled pallets
                    </Label>
                    <Input
                      id="calc-label"
                      data-testid="calc-label-input"
                      type="number"
                      min={0}
                      max={calcInputs.pallets}
                      value={calcInputs.labeling_pallets}
                      onChange={(e) =>
                        setCalcInputs({
                          ...calcInputs,
                          labeling_pallets: Math.max(0, parseInt(e.target.value || "0", 10) || 0),
                        })
                      }
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between p-3 rounded-lg border bg-slate-50">
                  <div>
                    <div className="text-sm font-medium text-slate-900">Documentation (warehouse receipt + CMR)</div>
                    <div className="text-xs text-slate-500">Flat fee, per consignment</div>
                  </div>
                  <Switch
                    data-testid="calc-doc-switch"
                    checked={calcInputs.include_documentation}
                    onCheckedChange={(v) => setCalcInputs({ ...calcInputs, include_documentation: v })}
                  />
                </div>
              </div>
            </div>

            {/* Result */}
            <div className="lg:col-span-2">
              <div className="sticky top-24 bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-6 lg:p-8 text-white shadow-xl">
                <div className="flex items-center justify-between mb-6">
                  <div className="text-sm uppercase tracking-wider text-blue-200">Indicative Total</div>
                  {calcLoading && <Loader2 className="w-4 h-4 animate-spin text-blue-200" />}
                </div>

                {calcError ? (
                  <div className="text-red-200 text-sm mb-4">{calcError}</div>
                ) : (
                  <div className="mb-6" data-testid="calc-total">
                    <div className="text-5xl font-bold tracking-tight">
                      €{calcResult ? calcResult.total_eur.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
                    </div>
                    <div className="text-blue-200 text-sm mt-1">
                      {calcInputs.pallets} pallets · {calcInputs.storage_days} days · handling {handlingLabel}
                    </div>
                    {calcResult?.min_charge_applied && (
                      <div className="mt-2 text-xs text-amber-200 bg-amber-500/20 inline-block px-2 py-0.5 rounded">
                        Minimum charge applied (€{calcResult.min_charge_eur})
                      </div>
                    )}
                  </div>
                )}

                {calcResult && !calcError && (
                  <div className="space-y-2 text-sm mb-6 pb-6 border-b border-blue-500/40">
                    <div className="flex justify-between">
                      <span className="text-blue-200">Storage</span>
                      <span className="font-medium">€{calcResult.breakdown.storage.total_eur.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-200">Handling</span>
                      <span className="font-medium">€{calcResult.breakdown.handling.total_eur.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-200">Extras</span>
                      <span className="font-medium">€{calcResult.breakdown.extras.total_eur.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-200">Documentation</span>
                      <span className="font-medium">€{calcResult.breakdown.documentation_eur.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                <Button
                  data-testid="open-lead-modal-btn"
                  onClick={() => {
                    setLeadResult(null);
                    setShowLeadModal(true);
                  }}
                  className="w-full bg-white text-blue-700 hover:bg-blue-50 font-semibold gap-2"
                  size="lg"
                >
                  Request Formal Offer
                  <ArrowRight className="w-4 h-4" />
                </Button>

                <p className="mt-4 text-xs text-blue-200 leading-relaxed">
                  Indicative only. Final offer depends on cargo type, dimensions, customs status and site survey.
                  No credit card required.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lead Modal */}
      {showLeadModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
          onClick={() => !leadSubmitting && setShowLeadModal(false)}
          data-testid="lead-modal"
        >
          <div
            className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6 lg:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => !leadSubmitting && setShowLeadModal(false)}
              className="absolute top-4 right-4 p-1 rounded-full hover:bg-slate-100"
              aria-label="Close"
              data-testid="close-lead-modal-btn"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>

            {leadResult ? (
              <div className="text-center py-6" data-testid="lead-success">
                <div className="mx-auto w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <CheckCircle className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Request sent!</h3>
                <p className="text-slate-600 mb-4">
                  Your reference is <span className="font-mono font-semibold text-blue-700">{leadResult.lead_number}</span>.
                  A confirmation has been sent to your email.
                </p>
                <p className="text-sm text-slate-500 mb-6">
                  Our team will come back within 24 business hours with a binding offer.
                </p>
                <Button onClick={() => setShowLeadModal(false)} className="w-full">Close</Button>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-bold text-slate-900 mb-1">Request a formal offer</h3>
                <p className="text-sm text-slate-500 mb-5">
                  Based on your inputs · Indicative total{" "}
                  <span className="font-semibold text-blue-700">
                    €{calcResult?.total_eur?.toFixed(2) ?? "—"}
                  </span>
                </p>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="lead-name" className="text-sm">Full name *</Label>
                    <Input
                      id="lead-name"
                      data-testid="lead-name-input"
                      value={leadForm.name}
                      onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                      placeholder="Jane Doe"
                      className="mt-1.5"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="lead-email" className="text-sm">Email *</Label>
                      <Input
                        id="lead-email"
                        data-testid="lead-email-input"
                        type="email"
                        value={leadForm.email}
                        onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                        placeholder="jane@company.com"
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lead-phone" className="text-sm">Phone</Label>
                      <Input
                        id="lead-phone"
                        data-testid="lead-phone-input"
                        value={leadForm.phone}
                        onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                        placeholder="+386 1 234 5678"
                        className="mt-1.5"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="lead-company" className="text-sm">Company</Label>
                    <Input
                      id="lead-company"
                      data-testid="lead-company-input"
                      value={leadForm.company}
                      onChange={(e) => setLeadForm({ ...leadForm, company: e.target.value })}
                      placeholder="Acme Logistics"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lead-notes" className="text-sm">Additional notes</Label>
                    <Textarea
                      id="lead-notes"
                      data-testid="lead-notes-input"
                      value={leadForm.notes}
                      onChange={(e) => setLeadForm({ ...leadForm, notes: e.target.value })}
                      placeholder="Any special requirements, commodity, dangerous goods, temperature, etc."
                      rows={3}
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div className="mt-6 flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowLeadModal(false)}
                    disabled={leadSubmitting}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={submitLead}
                    disabled={leadSubmitting}
                    className="flex-1 gap-2"
                    data-testid="submit-lead-btn"
                  >
                    {leadSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Send request
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                </div>
                <p className="mt-4 text-xs text-slate-500 text-center">
                  By submitting, you agree to be contacted by Europacific d.o.o. about your request.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Services */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Our Services</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Comprehensive logistics solutions tailored to your supply chain needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-xl border p-6 hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{service.title}</h3>
                <p className="text-slate-600 text-sm">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Value-Added Services
              </h2>
              <p className="text-slate-600 mb-8">
                Request additional warehouse services directly through your customer portal. 
                Our team will process your request and keep you updated in real-time.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {additionalServices.map((service, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-slate-700">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>{service}</span>
                  </div>
                ))}
              </div>
              <Link to="/wms/customer" className="inline-block mt-8">
                <Button className="gap-2">
                  Request Services Online
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="bg-white rounded-xl border p-6">
              <h3 className="font-semibold mb-4">Quick Service Request</h3>
              <p className="text-sm text-slate-500 mb-4">
                Login to your customer portal to request any of these services:
              </p>
              <ul className="space-y-2">
                {[
                  "Palletisation & stretch foiling",
                  "Additional cargo photography",
                  "Warehouse receipt / dispatch note",
                  "Damage inspection & reporting",
                  "Custom labeling & marking",
                  "Quality control checks",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* API Integration Section */}
      <section className="py-20 px-4 bg-slate-900" id="api">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-900 text-blue-300 text-sm font-medium mb-4">
              <Code className="w-4 h-4" />
              Developer API
            </span>
            <h2 className="text-3xl font-bold text-white mb-4">
              LCL API Integration
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Connect your ERP or TMS directly to myCargoCFS. Push orders automatically and receive real-time status updates.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* API Features */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-400" />
                  Quick Integration
                </h3>
                <ul className="space-y-3">
                  {[
                    "REST API with JSON payloads",
                    "API Key authentication",
                    "Real-time order status updates",
                    "Webhook notifications",
                    "Sandbox environment for testing",
                  ].map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-slate-300">
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4">Available Endpoints</h3>
                <div className="space-y-3 font-mono text-sm">
                  <div className="flex items-center gap-3">
                    <span className="px-2 py-1 bg-green-900 text-green-400 rounded text-xs">GET</span>
                    <span className="text-slate-300">/api/v1/test</span>
                    <span className="text-slate-500 text-xs">Test connection</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="px-2 py-1 bg-blue-900 text-blue-400 rounded text-xs">POST</span>
                    <span className="text-slate-300">/api/v1/orders</span>
                    <span className="text-slate-500 text-xs">Create order</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="px-2 py-1 bg-green-900 text-green-400 rounded text-xs">GET</span>
                    <span className="text-slate-300">/api/v1/orders/{'{id}'}</span>
                    <span className="text-slate-500 text-xs">Get status</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="px-2 py-1 bg-green-900 text-green-400 rounded text-xs">GET</span>
                    <span className="text-slate-300">/api/v1/docs</span>
                    <span className="text-slate-500 text-xs">Documentation</span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">Test API Key</h3>
                <p className="text-blue-100 text-sm mb-4">
                  Use this key for testing and development:
                </p>
                <div className="bg-black/30 rounded-lg p-3 font-mono text-sm flex items-center justify-between">
                  <code className="text-yellow-300">test_key_logitude_2026</code>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText('test_key_logitude_2026');
                    }}
                    className="text-blue-200 hover:text-white p-1"
                    title="Copy to clipboard"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>

            {/* Code Example */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 bg-slate-900 border-b border-slate-700">
                  <Terminal className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-400">Example: Create Order</span>
                </div>
                <pre className="p-4 text-sm overflow-x-auto">
                  <code className="text-slate-300">
{`curl -X POST ${API_URL}/wms/api/v1/orders \\
  -H "X-API-Key: test_key_logitude_2026" \\
  -H "Content-Type: application/json" \\
  -d '{
    "source_system": "logitude",
    "source_id": "LOG-12345",
    "customer_name": "Import Company Ltd",
    "reference_number": "REF-2026-001",
    "bl_numbers": ["MSCUTEST123456"],
    "cargo_description": "Electronics",
    "packages": 25,
    "gross_weight_kg": 1500,
    "volume_cbm": 8.5,
    "customer_email": "ops@company.com",
    "direction": "inbound"
  }'`}
                  </code>
                </pre>
                <div className="px-4 py-3 bg-slate-900 border-t border-slate-700">
                  <p className="text-xs text-slate-500">Response:</p>
                  <pre className="text-xs mt-2">
                    <code className="text-green-400">
{`{
  "success": true,
  "order_number": "CFS-202603-0001",
  "order_id": "uuid-here"
}`}
                    </code>
                  </pre>
                </div>
              </div>

              <div className="mt-6 flex gap-4">
                <a 
                  href={`${API_URL}/wms/api/v1/docs`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <Button variant="outline" className="w-full bg-transparent text-white border-slate-600 hover:bg-slate-800">
                    <FileText className="w-4 h-4 mr-2" />
                    View Full Documentation
                  </Button>
                </a>
                <a href="mailto:consulting@cargo-cfs.com?subject=API%20Access%20Request">
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    Request Production Key
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to streamline your logistics?
          </h2>
          <p className="text-blue-100 mb-8 text-lg">
            Contact us today or login to your customer portal to get started
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/wms/customer">
              <Button size="lg" variant="secondary" className="gap-2">
                Customer Portal
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <a href="https://cargo-cfs.com" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="bg-transparent text-white border-white hover:bg-white/10 gap-2">
                Learn More
                <ExternalLink className="w-4 h-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-14 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <svg viewBox="0 0 56 48" className="w-11 h-10" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 22 L28 6 L52 22" fill="none" stroke="#60a5fa" strokeWidth="2.5" strokeLinejoin="round" />
                  <rect x="22" y="22" width="12" height="18" fill="none" stroke="#60a5fa" strokeWidth="2.5" />
                  <rect x="24" y="24" width="8" height="7" fill="#6fbf3a" />
                  <line x1="8" y1="22" x2="8" y2="40" stroke="#60a5fa" strokeWidth="2.5" />
                  <line x1="48" y1="22" x2="48" y2="40" stroke="#60a5fa" strokeWidth="2.5" />
                  <line x1="4" y1="40" x2="52" y2="40" stroke="#60a5fa" strokeWidth="2.5" />
                  <circle cx="40" cy="42" r="2.2" fill="#60a5fa" />
                  <circle cx="46" cy="42" r="2.2" fill="#60a5fa" />
                </svg>
                <div>
                  <div className="font-bold text-xl text-white tracking-tight">Cargo<span className="text-blue-400">CFS</span></div>
                  <div className="text-xs text-blue-300 italic -mt-0.5">storage simplified</div>
                </div>
              </div>
              <p className="text-sm leading-relaxed">
                Public customs bonded warehouse providing comprehensive logistics
                solutions in Slovenia.
              </p>
              <p className="text-xs text-slate-500 mt-4">
                ID for VAT: <span className="text-slate-400">SI25262742</span><br/>
                Registry no.: <span className="text-slate-400">8164614</span>
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <Phone className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-400" />
                  <span>
                    <a href="tel:+38656205343" className="hover:text-white">+386 (0)5 6205 343</a>
                    <span className="block text-xs text-slate-500">Office</span>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Phone className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-400" />
                  <span>
                    <a href="tel:+38640736164" className="hover:text-white">+386 40 736 164</a>
                    <span className="block text-xs text-slate-500">Mobile — Mateja Tomic</span>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Mail className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-400" />
                  <a href="mailto:mateja@cargo-cfs.com" className="hover:text-white break-all">mateja@cargo-cfs.com</a>
                </li>
                <li className="flex items-start gap-2">
                  <Globe className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-400" />
                  <a href="https://www.cargo-cfs.com" target="_blank" rel="noopener noreferrer" className="hover:text-white">
                    www.cargo-cfs.com
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-400" /> Registered Office
              </h4>
              <address className="not-italic text-sm leading-relaxed">
                Cargo CFS d.o.o.<br/>
                Ankaranska cesta 5<br/>
                6000 Koper<br/>
                Slovenia
              </address>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Warehouse className="w-4 h-4 text-blue-400" /> Warehouse
              </h4>
              <address className="not-italic text-sm leading-relaxed">
                Cargo CFS d.o.o.<br/>
                Dekani 3a<br/>
                6271 Dekani<br/>
                Slovenia
              </address>
              <ul className="mt-4 space-y-1 text-sm">
                <li><Link to="/wms/customer" className="hover:text-white">Customer Portal</Link></li>
                <li><Link to="/wms/app" className="hover:text-white">Staff Login</Link></li>
                <li><a href="#api" className="hover:text-white">API Integration</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-10 pt-6 text-center text-xs text-slate-500">
            <p>&copy; {new Date().getFullYear()} Cargo CFS d.o.o. All rights reserved.</p>
            <p className="mt-1">Powered by Europacific Shipping · <a href="mailto:mateja@cargo-cfs.com" className="hover:text-white">mateja@cargo-cfs.com</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
